import { getFragmentData, graphql } from '@e-socna/data-layer';
import { EMPTY_ID } from '@e-socna/empty-client';
import type { Id } from '@e-socna/data-layer';

import { useGraphQLQuery } from '../../../hooks/useGraphQLQuery.hook';

import {
  Access_ActionAccess_Fragment,
  Access_Answer_Fragment,
} from '../fragments/access-answer.fragment';
import { gateOf, stateChangeGateOf } from '../utils/access-gate.util';
import type { AccessGate } from '../types/access-gate.type';

import { useRefetchOnChange } from './useRefetchOnChange.hook';

export const ReportInfoAccess_QueryDocument = graphql(`
  query ReportInfoAccess_Query(
    $id: Id!
    $update: String!
    $updateDocument: String!
    $part: String!
  ) {
    findOneMission(id: $id) {
      _id
      access {
        update: can(scope: $update) {
          ...Access_Answer_Fragment
        }
        updateDocument: can(scope: $updateDocument) {
          ...Access_Answer_Fragment
        }
        actions(part: $part) {
          ...Access_ActionAccess_Fragment
        }
      }
    }
  }
`);

export interface ReportInfoAccess {
  // When the answers were received (0 before): a page decides on answers received after it opened.
  readonly answeredAt: number;
  readonly update: AccessGate;
  readonly updateDocument: AccessGate;
  readonly changeState: AccessGate;
}

// What the user may do on a report info of a mission (its access part `report:<id>`).
export const useReportInfoAccess = (
  missionId: Id | undefined,
  reportInfoId: Id,
  version?: unknown,
): ReportInfoAccess => {
  const part = `report:${reportInfoId}`;
  const { data, refetch, dataUpdatedAt } = useGraphQLQuery(
    ReportInfoAccess_QueryDocument,
    {
      id: missionId ?? EMPTY_ID,
      update: `${part}:update`,
      updateDocument: `${part}:updateRelatedDocument`,
      part,
    },
    { enabled: !!missionId },
  );

  useRefetchOnChange(refetch, version);

  const access = data?.findOneMission.access;

  return {
    answeredAt: dataUpdatedAt,
    update: gateOf(getFragmentData(Access_Answer_Fragment, access?.update)),
    updateDocument: gateOf(
      getFragmentData(Access_Answer_Fragment, access?.updateDocument),
    ),
    changeState: stateChangeGateOf(
      getFragmentData(Access_ActionAccess_Fragment, access?.actions),
    ),
  };
};
